import { memo, useCallback, useMemo, ChangeEvent } from 'react';
import classNames from 'classnames';
import Picker from 'react-mobile-picker';
import { subMinutes, subHours, subDays, subWeeks, subMonths, subYears, format } from 'date-fns';
import {
  V2QsRelativeOnsetProps,
  V2QsRelativeOnsetValue,
  V2QsRelativeOnsetUnit,
  defaultUnitOptions,
  defaultV2QsRelativeOnsetProps,
} from './types';

/**
 * 단위에 따라 날짜 계산
 */
function calculateDate(baseDate: Date, duration: number, unit: V2QsRelativeOnsetUnit): Date {
  switch (unit) {
    case 'minute':
      return subMinutes(baseDate, duration);
    case 'hour':
      return subHours(baseDate, duration);
    case 'day':
      return subDays(baseDate, duration);
    case 'week':
      return subWeeks(baseDate, duration);
    case 'month':
      return subMonths(baseDate, duration);
    case 'year':
      return subYears(baseDate, duration);
    default:
      return baseDate;
  }
}

/**
 * 기본 날짜 포맷 함수 (V1 PatientDateFormat과 동일)
 * - minute/hour: 'MMMM do yyyy EEEE hh aa' (December 10th 2025 Wednesday 06 PM)
 * - day/week: 'MMMM do yyyy EEEE' (December 10th 2025 Wednesday)
 * - month: 'MMMM yyyy' (December 2025)
 * - year: 'yyyy' (2025)
 */
function defaultFormatDate(date: Date, unit: V2QsRelativeOnsetUnit): string {
  switch (unit) {
    case 'minute':
    case 'hour':
      return format(date, 'MMMM do yyyy EEEE hh aa'); // V1 PatientDateHourFormat
    case 'day':
    case 'week':
      return format(date, 'MMMM do yyyy EEEE'); // V1 PatientDateFormat
    case 'month':
      return format(date, 'MMMM yyyy'); // Month and year
    case 'year':
      return format(date, 'yyyy'); // Year only
    default:
      return format(date, 'MMMM do yyyy EEEE');
  }
}

/**
 * V2QsRelativeOnset 컴포넌트
 *
 * 상대적 시간 입력 컴포넌트 (예: "2시간 전", "3일 전")
 * V1 QsInputRelativeOnset의 V2 버전으로 순수 UI만 담당
 *
 * 주요 기능:
 * - 숫자 입력 + 단위 선택 (Picker 휠)
 * - 단위: minute, hour, day, week, month, year
 * - 선택한 시간에 따라 실제 날짜/시간 계산 및 표시
 */
function V2QsRelativeOnset(props: V2QsRelativeOnsetProps) {
  const {
    value,
    onChange,
    baseDate = new Date(),
    defaultUnit = defaultV2QsRelativeOnsetProps.defaultUnit!,
    unitOptions = defaultUnitOptions,
    disabled = defaultV2QsRelativeOnsetProps.disabled,
    readOnly = defaultV2QsRelativeOnsetProps.readOnly,
    hasError = defaultV2QsRelativeOnsetProps.hasError,
    errorMessage,
    showCalculatedDate = defaultV2QsRelativeOnsetProps.showCalculatedDate,
    formatCalculatedDate = defaultFormatDate,
    className,
    inputClassName,
    pickerClassName,
  } = props;

  // 현재 값
  const duration = value?.duration ?? 0;
  const unit = value?.unit ?? defaultUnit;

  // 계산된 날짜 (V1처럼 duration이 0이어도 현재 날짜 표시)
  const calculatedDate = useMemo(() => {
    return calculateDate(baseDate, duration, unit);
  }, [baseDate, duration, unit]);

  // 포맷된 날짜 문자열
  const formattedDate = useMemo(() => {
    if (!calculatedDate) return null;
    return formatCalculatedDate(calculatedDate, unit);
  }, [calculatedDate, unit, formatCalculatedDate]);

  // 단위 라벨 가져오기
  const getUnitLabel = useCallback(
    (unitValue: V2QsRelativeOnsetUnit, isPlural: boolean): string => {
      const option = unitOptions.find((opt) => opt.value === unitValue);
      if (!option) return unitValue;
      return isPlural ? option.labelPlural : option.labelSingular;
    },
    [unitOptions]
  );

  // duration 변경
  const handleDurationChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      if (disabled || readOnly) return;

      const newDuration = parseInt(e.target.value, 10) || 0;
      const newCalculatedDate = newDuration > 0 ? calculateDate(baseDate, newDuration, unit) : null;

      onChange?.({
        duration: newDuration,
        unit,
        calculatedDate: newCalculatedDate?.toISOString(),
      });
    },
    [onChange, disabled, readOnly, baseDate, unit]
  );

  // unit 변경 (Picker에서)
  const handlePickerChange = useCallback(
    (pickerValue: { unit: V2QsRelativeOnsetUnit }) => {
      if (disabled || readOnly) return;

      const newUnit = pickerValue.unit;
      let newDuration = duration;

      // duration이 0이고 단위가 변경되면 1로 설정
      if (duration === 0 && newUnit !== 'minute') {
        newDuration = 1;
      }

      const newCalculatedDate = newDuration > 0 ? calculateDate(baseDate, newDuration, newUnit) : null;

      onChange?.({
        duration: newDuration,
        unit: newUnit,
        calculatedDate: newCalculatedDate?.toISOString(),
      });
    },
    [onChange, disabled, readOnly, baseDate, duration]
  );

  // 복수형 여부
  const isPlural = duration > 1;

  return (
    <div className={classNames('flex flex-col gap-3', className)}>
      <div className="flex gap-4">
        {/* 숫자 입력 */}
        <div className="flex-1 flex items-center">
          <input
            type="tel"
            value={duration}
            onChange={handleDurationChange}
            onFocus={(e) => e.target.setSelectionRange(0, e.target.value.length)}
            disabled={disabled}
            readOnly={readOnly}
            className={classNames(
              'input input-bordered w-full',
              hasError && 'input-error',
              inputClassName
            )}
          />
        </div>

        {/* 단위 Picker */}
        <div
          className={classNames(
            'flex-1 border select-none',
            (disabled || readOnly) && 'opacity-50 pointer-events-none',
            pickerClassName
          )}
        >
          <Picker
            className="px-2"
            value={{ unit }}
            onChange={handlePickerChange}
            wheelMode="natural"
            height={110}
          >
            <Picker.Column name="unit">
              {unitOptions.map((option) => (
                <Picker.Item key={option.value} value={option.value}>
                  {({ selected }) => (
                    <div className={selected ? 'font-semibold' : 'opacity-70'}>
                      {getUnitLabel(option.value, isPlural)}
                    </div>
                  )}
                </Picker.Item>
              ))}
            </Picker.Column>
          </Picker>
        </div>
      </div>

      {/* 계산된 날짜 표시 */}
      {showCalculatedDate && formattedDate && (
        <div>
          <span className="font-semibold">{formattedDate}</span>
        </div>
      )}

      {/* 에러 메시지 */}
      {hasError && errorMessage && (
        <p className="text-error text-sm">{errorMessage}</p>
      )}
    </div>
  );
}

export default memo(V2QsRelativeOnset);
